import React, { useState, useContext } from 'react';
import { Button } from 'react-bootstrap';
import CommonConfigDialog from './CommonConfigDialog';
import { RequiredDialogInput, DialogInput, RequiredDialogSelect, DialogCheckbox, DialogIntegerInput } from '../../DialogInputs';
import { FunctionSelect } from '../../FunctionSelect';
import { handleInputChange } from '../../Utilities/DialogUtils';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import { useCalcFunctions, useLabFunctions, useOffspecFunctions } from '../../Utilities/Hooks';
import useAlert from '../../Alerts/useAlert';
import PrivilegeContext from '../../Contexts/PrivilegeContext';

function QualityEditDialog({ dialogData, setDialogData, showDialog, closeDialog, businessUnits }) {
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const [fieldErrors, setFieldErrors] = useState({});
    const calcFunctions = useCalcFunctions();
    const labFunctions = useLabFunctions();
    const offspecFunctions = useOffspecFunctions();

    const [loading, setLoading] = useState(false);
    const { canConfigure } = useContext(PrivilegeContext);

    const saveQuality = (quality) => {
        let promise = new Promise((resolve, reject) => {
            let url = `api/v1/quality`;
            if (quality.qualityId != null) {
                url += `/${quality.qualityId}`;
            }

            if (quality.defaultDecimalPlaces === "") {
                quality.defaultDecimalPlaces = null;
            }

            if (quality.specificGravity === "") {
                quality.specificGravity = null;
            }

            if (quality.displayOrder === "") {
                quality.displayOrder = null;
            }

            fetchDataAuthenticated(url, "POST", JSON.stringify(quality))
                .then(response => {
                    if (!response.ok) {
                        return response.json().then(error => Promise.reject(error));
                    }
                    return response;
                })
                .then(response => response.json())
                .then(data => {
                    if (data.errors !== null && data.errors !== undefined) {
                        reject(data);
                    }
                    resolve(data);
                })
                .catch(error => {
                    setAlert("danger", "Error", `Failed To Create Quality : \n${error.detail}`);
                });

        });

        return promise;
    };

    const onChange = (e) => {
        handleInputChange(e, dialogData, setDialogData, fieldErrors, setFieldErrors);
    };

    const onChangeFunction = (field, value, valid, validationMessage) => {
        setDialogData({
            ...dialogData,
            [field]: value
        });

        if (valid) {
            setFieldErrors({
                ...fieldErrors,
                [field]: null
            });
        }
        else {
            setFieldErrors({
                ...fieldErrors,
                [field]: validationMessage
            });
        }
    };

    const onChangeParams = (field, functionNameWithParams) => {
        setFieldErrors({
            ...fieldErrors,
            [field]: null
        });

        setDialogData({
            ...dialogData,
            [field]: functionNameWithParams
        });
    }

    const cloneRecipe = async () => {
        setLoading(true);
        let url = `api/v1/quality/${dialogData.qualityId}/clone`;

        await fetchDataAuthenticated(url, "POST")
            .then(async response => {
                if (response.ok) {
                    return response.json();
                }
                throw new Error(await response.text());
            })
            .then(data => {
                setDialogData({ ...data });
                setAlert("success", "Copied", "Quality has been copied");
            })
            .catch(error => {
                setAlert("danger", "Error", `Failed to clone quality. ${error}`);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const CloneQualityButton = () => {
        return canConfigure
            ?
            <Button className="me-auto" variant="secondary" onClick={cloneRecipe}>
                Clone Quality
            </Button>
            : undefined;
    };

    const QualityFunctionSelect = ({ controlId, title, name, functions }) => {
        return !loading
            ?
            <FunctionSelect controlId={controlId} title={title} name={name} functions={functions} onChangeFunction={onChangeFunction} onChangeParams={onChangeParams} value={dialogData[name]} error={fieldErrors[name]} />
            : undefined;
    };

    return (
        <CommonConfigDialog title={"Quality - " + dialogData.name} dialogData={dialogData} showDialog={showDialog} closeDialog={closeDialog} save={saveQuality} fieldErrors={fieldErrors} setFieldErrors={setFieldErrors} wideDialog={true} FooterButtons={CloneQualityButton}>
            <RequiredDialogInput controlId="formQualityName" title="Name" name="name" value={dialogData.name} error={fieldErrors.name} onChange={onChange} />
            <DialogInput controlId="formQualityDescription" title="Description" name="description" value={dialogData.description} error={fieldErrors.description} onChange={onChange} required={false} />
            <DialogInput controlId="formQualityType" title="Type" name="type" value={dialogData.type} error={fieldErrors.type} onChange={onChange} required={false} />
            <DialogInput controlId="formQualityUnits" title="Units" name="units" value={dialogData.units} error={fieldErrors.units} onChange={onChange} />
            <DialogCheckbox controlId="formQualityIsNumeric" title="Numeric?" name="isNumeric" checked={dialogData.isNumeric} error={fieldErrors.isNumeric} onChange={onChange} />
            <DialogIntegerInput controlId="formQualityDefaultDPs" title="Default DPs" name="defaultDecimalPlaces" value={dialogData.defaultDecimalPlaces} error={fieldErrors.defaultDecimalPlaces} onChange={onChange} />
            <RequiredDialogInput controlId="formQualityDefaultValue" title="Default Value To Use" name="defaultValueToUse" value={dialogData.defaultValueToUse} error={fieldErrors.defaultValueToUse} onChange={onChange} />
            <DialogInput controlId="formQualityAnalysis" title="Analysis" name="analysis" value={dialogData.analysis} error={fieldErrors.analysis} onChange={onChange} helpText="Analysis configured for this quality at L3" />
            <DialogInput controlId="formQualityComponent" title="Component" name="component" value={dialogData.component} error={fieldErrors.component} onChange={onChange} helpText="Component configured for this quality at L3" />
            <DialogInput controlId="formQualityReleaseAnalysis" title="Release Analysis" name="releaseAnalysis" value={dialogData.releaseAnalysis} error={fieldErrors.releaseAnalysis} onChange={onChange} helpText="Analysis configured for this quality in Sample Manager for the release Sample" />
            <DialogInput controlId="formQualityReleaseComponent" title="Release Component" name="releaseComponent" value={dialogData.releaseComponent} error={fieldErrors.releaseComponent} onChange={onChange} helpText="Component configured for this quality in Sample Manager for the release Sample" />
            <DialogIntegerInput controlId="formQualityDisplayOrder" title="Display Order" name="displayOrder" value={dialogData.displayOrder} error={fieldErrors.displayOrder} onChange={onChange} />
            <DialogInput controlId="formQualityMin" title="Min Value" name="masterMinValue" value={dialogData.masterMinValue} error={fieldErrors.masterMinValue} onChange={onChange} />
            <DialogInput controlId="formQualityMax" title="Max Value" name="masterMaxValue" value={dialogData.masterMaxValue} error={fieldErrors.masterMaxValue} onChange={onChange} />
            <DialogInput controlId="formQualitySpecificGravity" title="Specific Gravity" name="specificGravity" value={dialogData.specificGravity} error={fieldErrors.specificGravity} onChange={onChange} />
            <RequiredDialogInput controlId="formQualityConfidenceValueMinMD" title="Confidence Value Min MD" name="confidenceValueMinMD" value={dialogData.confidenceValueMinMD} error={fieldErrors.confidenceValueMinMD} onChange={onChange} />
            <QualityFunctionSelect controlId="formQualityTransferCalc" title="Transfer Calc Func" name="transferCalculateFunction" functions={calcFunctions} />
            <QualityFunctionSelect controlId="formQualityTransferOffspec" title="Transfer Offspec Func" name="transferIsOffspecFunction" functions={offspecFunctions} />
            <QualityFunctionSelect controlId="formQualityBatchCalc" title="Batch Calc Func" name="batchCalculateFunction" functions={calcFunctions} />
            <QualityFunctionSelect controlId="formQualityBatchLabCalc" title="Batch Lab Calc Func" name="batchLabCalculateFunction" functions={labFunctions} />
            <QualityFunctionSelect controlId="formQualityLotCalc" title="Lot Calc Func" name="lotCalculateFunction" functions={calcFunctions} />
            <QualityFunctionSelect controlId="formQualityLotLabCalc" title="Lot Lab Calc Func" name="lotLabCalculateFunction" functions={labFunctions} />
            <QualityFunctionSelect controlId="formQualityLotOffspec" title="Lot Offspec Func" name="lotIsOffspecFunction" functions={offspecFunctions} />
            <QualityFunctionSelect controlId="formQualitySecondaryLabCalc" title="Secondary Lab Calc Func" name="secondaryLabCalculateFunction" functions={labFunctions} />
            <RequiredDialogSelect controlId="formQualityBusinessUnit" title="Business Unit" name="businessUnitId" value={dialogData.businessUnitId} error={fieldErrors.businessUnitId} onChange={onChange}>
                <option value="" key={0} >Please Select...</option>
                {businessUnits.map((bu) => <option value={bu.businessUnitId} key={bu.businessUnitId}>{bu.name}</option>)}
            </RequiredDialogSelect>
            <DialogCheckbox disabled={dialogData.qualityId === null} controlId="formQualityIsDeleted" title="Deleted?" name="isDeleted" checked={dialogData.isDeleted} error={fieldErrors.isDeleted} onChange={onChange} />
        </CommonConfigDialog>
    );
}

export default QualityEditDialog;